import React, { useEffect, useState } from "react";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  activeInactiveHelpTopics,
  deleteHelpTopics,
  getAllHelpTopics,
  getHelpCategoryDdl,
  getHelpTopicsFields,
  IHelpTopic,
} from "./HelpTopicsViewController";
import { ORDER_BY } from "../../utils/AppConstants";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
} from "../../assets/icons/SvgIconList";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import { ICDropDown } from "../../core-component/form";
import HelpTopicForm from "./HelpTopicsForm";

let timer: ReturnType<typeof setTimeout>;

const HelpTopics = () => {
  const { t } = UseTranslationHook();
  const [helpTopicsList, setHelpTopicsList] = useState<IHelpTopic[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [helpTopicsToEdit, setHelpTopicsToEdit] = useState<IHelpTopic>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [advanceSearch, setAdvanceSearch] = useState<string>();
  const [helpCategory, setHelpCategory] = useState<IOption<string>[]>([]);

  useEffect(() => {
    fetchHelpTopics({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    const fetchHelpCategoryDdl = async () => {
      await getHelpCategoryDdl(setHelpCategory);
    };

    fetchHelpCategoryDdl();
  }, []);

  const fetchHelpTopics = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllHelpTopics(
        setHelpTopicsList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleHelpTopicStatus = async (item: IHelpTopic) => {
    const newStatus =
      item.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One;
    const isUpdated = await activeInactiveHelpTopics(item.id, newStatus);
    if (isUpdated) {
      setHelpTopicsList(toggleStatus(helpTopicsList, item.id));
    }
  };

  const handleDelete = async (value: boolean) => {
    if (value && helpTopicsToEdit) {
      const data = await deleteHelpTopics(helpTopicsToEdit.id);
      if (data) {
        fetchHelpTopics(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setHelpTopicsToEdit(undefined);
  };

  const customActionView = (item: IHelpTopic) => {
    return (
      <div className="flex item-center justify-end w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setHelpTopicsToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setHelpTopicsToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setHelpTopicsToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const filterView = (
    <div className="w-full flex gap-4">
      <ICDropDown
        placeholder={t("help_category_name")}
        className="min-w-64"
        selected={advanceSearch}
        options={helpCategory}
        onSelect={(option) => setAdvanceSearch(option.data_value)}
        searchable
      />
      <ICButton
        onClick={() => setAdvanceSearch(undefined)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (helpTopicsToEdit) {
      setHelpTopicsToEdit(undefined);
    }
    if (status) {
      fetchHelpTopics(pagination);
    }
  };

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchHelpTopics(pagination);
        }}
        title={t("help_topics")}
      />
      <ICTable<IHelpTopic>
        isLoading={isLoading}
        totalFlex="grid grid-cols-9"
        fields={getHelpTopicsFields(customActionView, handleHelpTopicStatus, t)}
        data={helpTopicsList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchHelpTopics({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchHelpTopics({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchHelpTopics({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showForm && (
        <HelpTopicForm
          handleClose={handleCloseForm}
          helpTopicToEdit={helpTopicsToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && helpTopicsToEdit && (
        <ICConfirmationModal
          title={t("delete_help_topics")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", helpTopicsToEdit.topic_title],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default HelpTopics;
